import { createContext, useContext, useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { TavelButton } from '../base/Button';

// 1) Контекст
const ConfirmContext = createContext(null);

// 2) Хук, которым ты будешь пользоваться в любых компонентах
export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        throw new Error('useConfirm должен использоваться внутри <ConfirmProvider>');
    }
    return ctx;
};

// 3) Провайдер
export const ConfirmProvider = ({ children }) => {
    const [state, setState] = useState({
        open: false,
        title: '',
        content: '',
        confirmText: 'OK',
        cancelText: 'Отмена',
        confirmDisabled: false,
        extraText: '',
        extraDisabled: false,
        extraAction: null,
        // сюда можно ещё прокинуть severity, цвета кнопок и т.п.
        resolve: null,
    });

    const confirm = useCallback((options) => {
        return new Promise((resolve) => {
        setState({
            open: true,
            title: options.title || 'Подтверждение',
            content: options.content || '',
            confirmText: options.confirmText || 'OK',
            cancelText: options.cancelText || 'Отмена',
            confirmDisabled: Boolean(options.confirmDisabled),
            extraText: options.extraText || '',
            extraDisabled: Boolean(options.extraDisabled),
            extraAction: typeof options.onExtra === "function" ? options.onExtra : null,
            resolve,
        });
        });
    }, []);

    const handleClose = () => {
        setState((prev) => ({ ...prev, open: false, resolve: null }));
    };

    const handleCancel = () => {
        if (state.resolve) state.resolve(false);
        handleClose();
    };

    const handleOk = () => {
        if (state.resolve) state.resolve(true);
        handleClose();
    };

    const handleExtra = async () => {
        try {
            if (typeof state.extraAction === "function") {
                await state.extraAction();
            }
        } finally {
            if (state.resolve) state.resolve(false);
            handleClose();
        }
    };

    return (
        <ConfirmContext.Provider value={confirm}>
        {children}

        <Dialog
            open={state.open}
            onClose={handleCancel}
            // оформление самого "окна" (аналог твоего Box в Modal)
            PaperProps={{
                sx: {
                    backgroundColor: 'var(--background-color-fon)',
                    borderRadius: '34px',
                    width: '90%',
                    maxWidth: '720px',
                    p: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 0,
                    gap: 1,
                },
            }}
            
        >
            {state.title && <DialogTitle>{state.title}</DialogTitle>}
            <DialogContent>
            {state.content && (
                <DialogContentText sx={{ fontSize: 14 }}>
                {state.content}
                </DialogContentText>
            )}
            </DialogContent>
            <DialogActions data-modal-actions sx={{ p: 2, pt: 0, flexWrap: "wrap", gap: 1 }}>
            <TavelButton
                variant="contained"
                size="mediumsmall"
                bgColor="var(--grey-color)"
                hoverColor="var(--button-gray-hover)"
                activeColor="var(--button-gray-active)"
                onClick={handleCancel}
            >
                {state.cancelText}
            </TavelButton>
            <TavelButton
                variant="contained"
                size="mediumsmall"
                bgColor="var(--red-color)"
                hoverColor="var(--button-red-hover)"
                activeColor="var(--button-red-active)"
                onClick={handleOk}
                disabled={state.confirmDisabled}
            >
                {state.confirmText}
            </TavelButton>
            {state.extraText ? (
                <TavelButton
                    variant="contained"
                    size="mediumsmall"
                    bgColor="var(--red-color)"
                    hoverColor="var(--button-red-hover)"
                    activeColor="var(--button-red-active)"
                    onClick={handleExtra}
                    disabled={state.extraDisabled}
                >
                    {state.extraText}
                </TavelButton>
            ) : null}
            </DialogActions>
        </Dialog>
        </ConfirmContext.Provider>
    );
};
